/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { StyleSheet } from 'aphrodite';
import { inputFormatters } from '@blend/shared-react';
import { Dropdown, TextInput, Checkbox, composeValidators, isEmptyValue } from '@blend/enterprise-components';

import { NOTARY_ASSIGNABLE_PARTICIPANT_TITLES, ECLOSE_BORROWER_TITLES } from '../../../../../types/participant';
import { WrappedInput } from '@components/common/enterprise/WrapperInput/WrapperInput';
import { email, notBlankValidator, signatureHasAtLeastTwoWordsValidator } from '@components/form/TaskInputs/validators';
import { startCase } from '@utils/lodash';

export const styles = StyleSheet.create({
  fieldWrapper: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
  },
});

const roleOptions = NOTARY_ASSIGNABLE_PARTICIPANT_TITLES.map(c => ({ text: c, value: c }));

const borrowerRoleOptions = ECLOSE_BORROWER_TITLES.map(c => ({ text: c, value: c }));

export const getTitleInputConfig = (isBorrower: boolean) => {
  let customComponentProps;
  if (isBorrower) {
    customComponentProps = {
      options: [{ text: '- Select an option -', value: '' }, ...borrowerRoleOptions],
      valueRequired: true,
      disabled: true,
    };
  } else {
    customComponentProps = {
      options: [{ text: '- Select an option -', value: '' }, ...roleOptions],
      valueRequired: true,
    };
  }

  return {
    component: WrappedInput,
    containerStyles: [styles.fieldWrapper],
    customComponentProps,
    inputComponent: Dropdown,
    label: 'Role',
    labelSize: 'sm',
    name: 'title',
    validate: isEmptyValue(`Role is required`),
  };
};

export const secondaryTitleInputConfig = {
  component: WrappedInput,
  containerStyles: [styles.fieldWrapper],
  customComponentProps: { options: [{ text: '- Select an option -', value: '' }, ...roleOptions], valueRequired: true },
  inputComponent: Dropdown,
  labelSize: 'sm',
  validate: isEmptyValue(`Role is required`),
};

const NameInputConfigToLabelMap = new Map<string, string>([
  ['first', 'First Name'],
  ['middle', 'Middle Name (optional)'],
  ['last', 'Last Name'],
  ['suffix', 'Suffix (optional)'],
]);

export const getNameInputConfig = (type: 'first' | 'middle' | 'last' | 'suffix') => ({
  component: WrappedInput,
  containerStyles: [styles.fieldWrapper],
  customComponentProps: { type: 'text' },
  inputComponent: TextInput,
  label: NameInputConfigToLabelMap.get(type),
  labelSize: 'sm',
  name: `${type === 'suffix' ? `${type}` : `${type}Name`}`,
  validate: !['middle', 'suffix'].includes(type) ? notBlankValidator(`${startCase(type)} name is required`) : undefined,
});

export const getSignatureInputConfig = () => {
  const config = getNameInputConfig('first');
  return {
    ...config,
    label: `Signature`,
    name: `signature`,
    validate: composeValidators(notBlankValidator('Signature is required'), signatureHasAtLeastTwoWordsValidator),
  };
};

export const getSecondarySignatureInputConfig = () => {
  const config = getNameInputConfig('first');
  return {
    ...config,
    validate: composeValidators(notBlankValidator('Signature is required'), signatureHasAtLeastTwoWordsValidator),
  };
};

export const getEmailInputConfig = (isBorrower?: boolean) => {
  let validate;
  if (isBorrower) {
    validate = undefined;
  } else {
    validate = email;
  }
  return {
    component: WrappedInput,
    containerStyles: [styles.fieldWrapper],
    customComponentProps: { type: 'text', disabled: isBorrower },
    inputComponent: TextInput,
    label: 'Email address',
    labelSize: 'sm',
    name: 'email',
    validate,
  };
};

export const phoneNumberInputConfig = {
  component: WrappedInput,
  containerStyles: [styles.fieldWrapper],
  customComponentProps: { type: 'phone' },
  inputComponent: TextInput,
  label: 'Phone number (optional)',
  labelSize: 'sm',
  name: 'phoneNumber',
  parse: inputFormatters.PHONE.normalize,
};

export const isKBARequiredInputConfig = {
  component: WrappedInput,
  containerStyles: [styles.fieldWrapper],
  inputComponent: Checkbox,
  label: 'This participant must answer security questions',
  labelPosition: 'right',
  labelSize: 'sm',
  name: 'requiresKba',
  type: 'checkbox',
};
